import { useEffect, useMemo, useRef, useState } from 'react'

import type { ApolloClient } from '@apollo/client'
import type { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import {
  getPublicApiUrl,
  publicApiClient,
} from '~publish/clients/publicApiClient'
import type { ResultOf } from '~publish/graphql'

import { GOAL_LABELS, narrowTags, TYPE_LABELS } from '../labels'
import type { Goal, Tag, Type } from '../types'
import { PublicPostTemplatesQuery } from '../graphql/postTemplates'

const COLORS = [
  '255, 203, 49',
  '218, 255, 53',
  '99, 117, 255',
  '248, 149, 151',
  '87, 152, 255',
  '59, 252, 255',
]

export const PUBLIC_TEMPLATES_LIMIT = 200

export const getPublicTemplatesApiUrl = getPublicApiUrl

type ApiPublicTemplate = NonNullable<
  ResultOf<typeof PublicPostTemplatesQuery>['publicPostTemplates']
>['edges'][number]['node']

export type PublicPostTemplate = {
  id: string
  body: string
  color: string
  createdAt: string
  description: string
  emoji: string
  goal: Goal
  tags: Tag[]
  title: string
  type: Type
}

function mapTemplate(
  node: ApiPublicTemplate,
  index: number,
): PublicPostTemplate {
  return {
    id: node.id,
    title: node.title,
    body: node.body,
    description: node.description,
    emoji: node.emoji,
    type: TYPE_LABELS[node.type] ?? 'Tip',
    goal: GOAL_LABELS[node.goal] ?? 'Engagement',
    tags: narrowTags(node.tags),
    createdAt: node.createdAt,
    color: COLORS[index % COLORS.length],
  }
}

/** Preload the first page; the mounted library owns pagination. */
export function usePreloadPublicPostTemplates(
  client: ApolloClient = publicApiClient,
): void {
  useEffect(() => {
    // Keep the request alive across the loading frame's unmount. The library
    // handles failures when it reads or retries this optional preload.
    void client
      .query({
        query: PublicPostTemplatesQuery,
        variables: { first: PUBLIC_TEMPLATES_LIMIT },
      })
      .catch(() => undefined)
  }, [client])
}

export function usePublicPostTemplates(
  client: ApolloClient = publicApiClient,
): {
  loading: boolean
  templates: PublicPostTemplate[]
  error: ErrorLike | undefined
} {
  const { data, dataState, loading, error, fetchMore } = useQuery(
    PublicPostTemplatesQuery,
    {
      client,
      variables: { first: PUBLIC_TEMPLATES_LIMIT },
    },
  )

  const completeData = dataState === 'complete' ? data : undefined
  const pageInfo = completeData?.publicPostTemplates.pageInfo
  const hasNextPage = pageInfo?.hasNextPage ?? false
  const nextCursor = pageInfo?.endCursor
  const lastRequestedCursorRef = useRef<string | null>(null)
  const [paginationError, setPaginationError] = useState<ErrorLike>()
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)

  useEffect(() => {
    if (
      !hasNextPage ||
      !nextCursor ||
      nextCursor === lastRequestedCursorRef.current ||
      paginationError ||
      isFetchingNextPage
    ) {
      return
    }

    lastRequestedCursorRef.current = nextCursor
    setIsFetchingNextPage(true)

    void fetchMore({
      variables: { first: PUBLIC_TEMPLATES_LIMIT, after: nextCursor },
      updateQuery: (previous, { fetchMoreResult }) => ({
        publicPostTemplates: {
          ...fetchMoreResult.publicPostTemplates,
          edges: [
            ...previous.publicPostTemplates.edges,
            ...fetchMoreResult.publicPostTemplates.edges,
          ],
        },
      }),
    })
      .catch((fetchError: unknown) => {
        setPaginationError(
          fetchError instanceof Error
            ? fetchError
            : new Error('Failed to fetch all public templates'),
        )
      })
      .finally(() => {
        setIsFetchingNextPage(false)
      })
  }, [fetchMore, hasNextPage, isFetchingNextPage, nextCursor, paginationError])

  const templates = useMemo(
    () =>
      (completeData?.publicPostTemplates.edges ?? []).map((edge, index) =>
        mapTemplate(edge.node, index),
      ),
    [completeData],
  )
  const hasUnrequestedPage = Boolean(
    hasNextPage &&
    nextCursor &&
    nextCursor !== lastRequestedCursorRef.current &&
    !paginationError,
  )

  return {
    loading: loading || isFetchingNextPage || hasUnrequestedPage,
    templates,
    error: error ?? paginationError,
  }
}
